import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, EyeOff, Globe, Share2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["", "pending", "approved", "rejected", "hidden", "private"];
const STATUS_STYLE = {
  private: "bg-sand-200 text-charcoal-600", pending: "bg-terracotta-light text-terracotta-700",
  approved: "bg-sage/15 text-sage", rejected: "bg-red-50 text-red-600", hidden: "bg-sand-200 text-charcoal-500",
};

function AdminMedia({ item }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke;
    api.get(item.url.replace(/^\/api/, ""), { responseType: "blob" }).then((r) => { const o = URL.createObjectURL(r.data); revoke = o; setUrl(o); }).catch(() => {});
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [item.url]);
  if (!url) return <div className="h-32 w-44 rounded-lg bg-sand-100 animate-pulse" />;
  return item.kind === "video"
    ? <video src={url} controls muted preload="none" className="h-32 w-44 rounded-lg object-cover bg-black" />
    : <img src={url} alt={item.caption || ""} className="h-32 w-44 rounded-lg object-cover" />;
}

export default function ClientUploadsManager() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [status, setStatus] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (clientId) p.set("client_id", clientId);
    if (status) p.set("status", status);
    api.get(`/admin/trip-media/client-uploads?${p.toString()}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { api.get("/admin/trip-media/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [clientId, status]);

  const act = async (item, action) => {
    try { await api.post(`/admin/trip-media/${item.id}/${action}`); toast.success(`Marked ${action === "approve" ? "approved" : action}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="client-uploads-manager" className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <select data-testid="cu-filter-client" value={clientId} onChange={(e) => setClientId(e.target.value)} className="border border-sand-200 rounded-lg px-3 py-2 text-sm">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{`${c.name} (${c.email})`}</option>)}
        </select>
        <select data-testid="cu-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="border border-sand-200 rounded-lg px-3 py-2 text-sm">
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
        </select>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-charcoal-400" data-testid="cu-empty">No client uploads match this filter.</p>
      ) : items.map((item) => (
        <Card key={item.id} className="p-4 flex flex-col sm:flex-row gap-4" data-testid={`cu-item-${item.id}`}>
          <AdminMedia item={item} />
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-charcoal-800">{item.client_name || "Client"}</span>
              <span className="text-charcoal-400">{item.client_email}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[item.status] || ""}`} data-testid={`cu-status-${item.id}`}>{item.status}</span>
            </div>
            {item.caption && <p className="text-sm text-charcoal-600">“{item.caption}”</p>}
            <div className="flex flex-wrap gap-3 text-xs text-charcoal-500">
              <span className={`inline-flex items-center gap-1 ${item.consent_website ? "text-sage" : "text-charcoal-400"}`}><Globe size={13} /> Website {item.consent_website ? "✓" : "✗"}</span>
              <span className={`inline-flex items-center gap-1 ${item.consent_social ? "text-sage" : "text-charcoal-400"}`}><Share2 size={13} /> Social {item.consent_social ? "✓" : "✗"}</span>
            </div>
            {item.email_error && <p className="text-xs text-red-600" data-testid={`cu-email-error-${item.id}`}>Staff email failed: {item.email_error}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              <button data-testid={`cu-approve-${item.id}`} onClick={() => act(item, "approve")} disabled={!item.consent_website}
                title={item.consent_website ? "" : "Client has not consented to website use"}
                className="inline-flex items-center gap-1 rounded-full bg-sage text-white px-3 py-1.5 text-sm disabled:opacity-40"><Check size={14} /> Approve for website</button>
              <button data-testid={`cu-reject-${item.id}`} onClick={() => act(item, "reject")} className="inline-flex items-center gap-1 rounded-full border border-red-300 text-red-600 px-3 py-1.5 text-sm"><X size={14} /> Reject</button>
              <button data-testid={`cu-hide-${item.id}`} onClick={() => act(item, "hide")} className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm"><EyeOff size={14} /> Hide</button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
